import React from "react";
import { useTranslation } from "react-i18next";
import { MediaItem } from "../../../domain";
import { fmt, fmtDate, fmtWatchTime, fmtTotalWatchTime, fmtDuration, fmtSkipRate, getIndicator, mediaTypeLabelKey } from "./helpers";

// ── type badge class ──────────────────────────────────────────────────────────

function typeBadgeClass(mediaType: string): string {
  switch (mediaType.toUpperCase()) {
    case "VIDEO":          return "type-badge reel";
    case "IMAGE":          return "type-badge photo";
    case "CAROUSEL_ALBUM": return "type-badge carousel";
    default:               return "type-badge reel";
  }
}

// ── small dot ─────────────────────────────────────────────────────────────────

function RcDot({ level }: { level: string }) {
  const cls = level === "green" ? "green" : level === "yellow" ? "yellow" : level === "blue" ? "blue" : "red";
  return <span className={`rc-dot ${cls}`} />;
}

// ── ext link icon ─────────────────────────────────────────────────────────────

const ExtLinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

// ── MediaRow ──────────────────────────────────────────────────────────────────

interface MediaRowProps { item: MediaItem; index: number; followersCount?: number | null }

export const MediaRow: React.FC<MediaRowProps> = ({ item, followersCount }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en-US" : "ru-RU";

  const isReel = item.media_type === "VIDEO";
  const viewsVal = item.views ?? item.plays ?? null;
  const viewsInd = getIndicator("views", viewsVal);
  const watchSec = item.avg_watch_time_ms != null ? item.avg_watch_time_ms / 1000 : null;
  const watchInd = getIndicator("avg_watch_time_sec", watchSec);
  const reachRate = (item.reach != null && followersCount)
    ? (item.reach / followersCount) * 100
    : null;
  const reachInd = getIndicator("reach_rate", reachRate);
  const skipRateInd = getIndicator("skip_rate", item.skip_rate ?? null);

  const hasReelMetrics = isReel && (
    viewsVal != null || item.reach != null ||
    item.avg_watch_time_ms != null || item.total_watch_time_ms != null ||
    item.skip_rate != null || item.follows_from_reel != null
  );
  const hasCommercialContent = (item.skus?.length ?? 0) > 0 || (item.brand_mentions?.length ?? 0) > 0;
  const rowSpanValue = 1 + (hasReelMetrics ? 1 : 0) + (hasCommercialContent ? 1 : 0);

  return (
    <>
      <tr className={`post-row${(hasReelMetrics || hasCommercialContent) ? " post-row--has-sub" : ""}`}>
        {/* thumbnail */}
        <td rowSpan={rowSpanValue} style={{ width: 60, padding: "6px 10px" }}>
          {item.cover_url ? (
            <a href={item.permalink} target="_blank" rel="noopener noreferrer" className="p-thumb">
              <img src={item.cover_url} alt="cover" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
            </a>
          ) : (
            <a href={item.permalink} target="_blank" rel="noopener noreferrer" className="p-thumb">
              {isReel ? "▶" : item.media_type === "CAROUSEL_ALBUM" ? "◧" : "□"}
            </a>
          )}
        </td>
        {/* type */}
        <td>
          <span className={typeBadgeClass(item.media_type)}>
            {t(mediaTypeLabelKey(item.media_type))}
          </span>
        </td>
        {/* date */}
        <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: "var(--ink-pale)", whiteSpace: "nowrap" }}>
          {fmtDate(item.timestamp, locale)}
        </td>
        {/* caption */}
        <td>
          <span className="p-caption" title={item.caption ?? ""}>
            {item.caption?.substring(0, 120) || <span style={{ color: "var(--ink-pale)" }}>—</span>}
          </span>
        </td>
        {/* metrics */}
        <td className="p-num">{fmt(item.likes, locale)}</td>
        <td className="p-num">{fmt(item.comments, locale)}</td>
        <td className="p-num">{fmt(item.saves, locale)}</td>
        {/* link */}
        <td rowSpan={rowSpanValue} style={{ textAlign: "center" }}>
          <a href={item.permalink} target="_blank" rel="noopener noreferrer" className="p-link">
            <ExtLinkIcon />
          </a>
        </td>
      </tr>

      {/* reel metrics sub-row */}
      {hasReelMetrics && (
        <tr className="reel-sub">
          <td colSpan={6}>
            <div className="reel-sub-inner">
              {viewsVal != null && (
                <span className="rchip">
                  <RcDot level={viewsInd.level} />
                  {fmt(viewsVal, locale)}&nbsp;просм.
                </span>
              )}
              {item.reach != null && (
                <>
                  <span className="rchip-sep">·</span>
                  <span className="rchip">
                    <RcDot level={reachInd.level} />
                    {fmt(item.reach, locale)}&nbsp;охват
                    {reachRate != null && (
                      <span style={{ color: "var(--ink-pale)" }}>
                        &nbsp;({reachRate >= 100 ? `×${(reachRate / 100).toFixed(1).replace(".0", "")}` : reachRate.toFixed(1) + "%"})
                      </span>
                    )}
                  </span>
                </>
              )}
              {watchSec != null && (
                <>
                  <span className="rchip-sep">·</span>
                  <span className="rchip">
                    <RcDot level={watchInd.level} />
                    {fmtWatchTime(item.avg_watch_time_ms!, t("report.sec"))}&nbsp;ср.&nbsp;просм.
                  </span>
                </>
              )}
              {item.duration_ms != null && (
                <>
                  <span className="rchip-sep">·</span>
                  <span className="rchip">{fmtDuration(item.duration_ms)}&nbsp;длина</span>
                </>
              )}
              {item.total_watch_time_ms != null && (
                <>
                  <span className="rchip-sep">·</span>
                  <span className="rchip">
                    {fmtTotalWatchTime(item.total_watch_time_ms, t("report.sec"), t("report.min"))}&nbsp;суммарно
                  </span>
                </>
              )}
              {item.skip_rate != null && (
                <>
                  <span className="rchip-sep">·</span>
                  <span className="rchip">
                    <RcDot level={skipRateInd.level} />
                    Skip&nbsp;{fmtSkipRate(item.skip_rate, locale)}
                  </span>
                </>
              )}
              {item.follows_from_reel != null && (
                <>
                  <span className="rchip-sep">·</span>
                  <span className="rchip">
                    <RcDot level="blue" />
                    +{fmt(item.follows_from_reel, locale)}&nbsp;подп.
                  </span>
                </>
              )}
            </div>
          </td>
        </tr>
      )}

      {/* commercial content sub-row */}
      {hasCommercialContent && (
        <tr className="comm-row">
          <td colSpan={6}>
            <div className="comm-inner">
              <span className="comm-label">Коммерческий контент:</span>
              {(item.skus ?? []).map((sku, idx) => (
                <span key={`sku-${idx}`} className="sku-badge">{sku}</span>
              ))}
              {(item.brand_mentions ?? []).map((brand, idx) => (
                <span key={`brand-${idx}`} className="brand-badge">{brand}</span>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
